import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { brandClasses } from '../theme';

interface EditableTreeTitleProps {
  currentName: string;
  onSave: (newName: string) => Promise<void>;
  readonly?: boolean;
  currentDescription?: string;
  onSaveDescription?: (newDescription: string) => Promise<void>;
  readonlyDescription?: boolean;
}

export const EditableTreeTitle: React.FC<EditableTreeTitleProps> = ({
  currentName,
  onSave,
  readonly = false,
  currentDescription = '',
  onSaveDescription,
  readonlyDescription = false,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(currentName);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [descValue, setDescValue] = useState(currentDescription);
  const [isSavingDesc, setIsSavingDesc] = useState(false);
  const descOverlayRef = useRef<HTMLDivElement>(null);
  const descTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Close the description overlay when clicking outside it
  useEffect(() => {
    if (!isEditingDesc) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (descOverlayRef.current && !descOverlayRef.current.contains(e.target as Node)) {
        void commitDesc();
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingDesc, descValue, currentDescription]);

  const handleStartEdit = () => {
    if (!readonly) {
      setIsEditing(true);
      setEditValue(currentName);
      setError('');
    }
  };

  const handleSave = async () => {
    const trimmedValue = editValue.trim();
    if (!trimmedValue) {
      setError('Tree name cannot be empty');
      return;
    }
    if (trimmedValue === currentName) {
      setIsEditing(false);
      return;
    }
    try {
      setIsSaving(true);
      setError('');
      await onSave(trimmedValue);
      setIsEditing(false);
    } catch (_) {
      setError('Failed to save tree name');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(currentName);
    setError('');
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSave();
    else if (e.key === 'Escape') handleCancel();
  };

  const handleStartEditDesc = () => {
    if (!readonlyDescription && onSaveDescription) {
      setDescValue(currentDescription);
      setIsEditingDesc(true);
      // focus textarea after render
      requestAnimationFrame(() => descTextareaRef.current?.focus());
    }
  };

  const commitDesc = async () => {
    const trimmed = descValue.trim();
    setIsEditingDesc(false);
    if (trimmed === currentDescription.trim()) return;
    try {
      setIsSavingDesc(true);
      await onSaveDescription!(trimmed);
    } finally {
      setIsSavingDesc(false);
    }
  };

  const discardDesc = () => {
    setIsEditingDesc(false);
    setDescValue(currentDescription);
  };

  const handleDescKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      discardDesc();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void commitDesc();
    }
  };

  const canEditDesc = !readonlyDescription && !!onSaveDescription;

  if (isEditing) {
    return (
      <div className={`flex items-center gap-3 border-b px-10 py-4 ${brandClasses.subtleBorder} ${brandClasses.titleBarSurface}`}>
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleNameKeyDown}
          placeholder={t('treeTitle.placeholder', { ns: 'canvas' })}
          className="flex-1 text-4xl font-bold border-2 border-amber-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
          autoFocus
          disabled={isSaving}
        />
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-4 py-2 bg-green-600 text-white rounded font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          title={t('treeTitle.saveTitle', { ns: 'canvas' })}
        >
          ✓
        </button>
        <button
          onClick={handleCancel}
          disabled={isSaving}
          className="px-4 py-2 bg-gray-400 text-white rounded font-medium hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          title={t('treeTitle.cancelTitle', { ns: 'canvas' })}
        >
          ✕
        </button>
        {error && <span className="text-red-600 text-sm font-medium">{error}</span>}
      </div>
    );
  }

  return (
    // `relative overflow-visible` — the overlay card floats below without changing this bar's height
    <div className={`relative border-b px-10 py-4 ${brandClasses.subtleBorder} ${brandClasses.titleBarSurface}`}>
      {/* ── Title row ── */}
      <div className="flex items-center justify-between">
        <h1 className={`text-4xl font-bold leading-tight ${brandClasses.primaryText}`}>{currentName}</h1>
        {!readonly && (
          <button
            onClick={handleStartEdit}
            className={`rounded p-2 transition-colors ${brandClasses.mutedText} hover:bg-[#f0ece2] hover:text-[#4a453b]`}
            title={t('treeTitle.editAriaLabel', { ns: 'canvas' })}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19H4v-3L16.5 3.5z" />
            </svg>
          </button>
        )}
      </div>

      {/* ── Description row — fixed height, never changes ── */}
      <div className="h-5 mt-1">
        {canEditDesc ? (
          <button
            type="button"
            onClick={handleStartEditDesc}
            disabled={isSavingDesc}
            className={`max-w-full truncate text-left text-sm transition-colors disabled:opacity-50 ${
              currentDescription
                ? `${brandClasses.mutedText} hover:text-[#4a453b]`
                : `${brandClasses.subtleText} italic hover:text-[#4a453b]`
            }`}
            title={t('treeTitle.clickToEditAriaLabel', { ns: 'canvas' })}
          >
            {currentDescription || t('treeTitle.addDescriptionPlaceholder', { ns: 'canvas' })}
          </button>
        ) : currentDescription ? (
          <p className={`truncate text-sm ${brandClasses.mutedText}`}>{currentDescription}</p>
        ) : null}
      </div>

      {/* ── Description overlay — absolutely positioned, floats over canvas ── */}
      {isEditingDesc && (
        <div
          ref={descOverlayRef}
          className={`absolute left-0 right-0 top-full z-50 mx-0 rounded-b-xl border-x border-b px-10 pb-4 pt-3 shadow-lg ${brandClasses.subtleBorder} ${brandClasses.titleBarSurface}`}
        >
          <textarea
            ref={descTextareaRef}
            value={descValue}
            onChange={(e) => setDescValue(e.target.value)}
            onKeyDown={handleDescKeyDown}
            rows={3}
            placeholder={t('treeTitle.descTextareaPlaceholder', { ns: 'canvas' })}
            className={`w-full resize-none rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#8fa36b] ${brandClasses.strongBorder} bg-white ${brandClasses.primaryText}`}
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              onClick={() => void commitDesc()}
              disabled={isSavingDesc}
              className="rounded bg-[#8fa36b] px-3 py-1 text-sm font-medium text-white hover:bg-[#7a8f5a] disabled:opacity-50"
            >
              {isSavingDesc ? t('treeTitle.savingLabel', { ns: 'canvas' }) : t('buttons.save', { ns: 'common' })}
            </button>
            <button
              onClick={discardDesc}
              disabled={isSavingDesc}
              className={`rounded px-3 py-1 text-sm font-medium ${brandClasses.mutedText} hover:bg-[#f0ece2] disabled:opacity-50`}
            >
              {t('buttons.cancel', { ns: 'common' })}
            </button>
            <span className={`ms-auto text-xs ${brandClasses.subtleText}`}>{t('treeTitle.saveHint', { ns: 'canvas' })}</span>
          </div>
        </div>
      )}
    </div>
  );
};
